import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { IVideoClip, ResourceType, ResourceStatus, IImageGeneration } from '@packages/global';
import { PhotoIcon, XMarkIcon, ScissorsIcon, ArrowUpTrayIcon, TrashIcon } from '@heroicons/react/24/outline';
import { videoClipResourceApi } from '../../../services/user/videoClipResource.api';
import { videoClipApi } from '../../../services/user/videoclip.api';
import { useModels } from '../../../hooks/useModels';
import { useDialog } from '../../../hooks/useDialog';
import { useVideoEditor } from '../context/VideoEditorContext';
import ImageUpload from '../../common/ImageUpload';
import ImageSelector from '../../common/ImageSelector';
import ImageCropper from '../../common/ImageCropper';

interface ImagesTabProps {
  videoClip: IVideoClip;
  refreshVideoClip?: (clipId: string) => Promise<void>;
  onFrameTypeChange?: (type: 'start' | 'end' | 'storyboard') => void;
  activeFrameType?: 'start' | 'end' | 'storyboard';
  onRequestClose?: () => void;
}

export default function ImagesTab({ videoClip, refreshVideoClip, onFrameTypeChange, activeFrameType = 'start', onRequestClose }: ImagesTabProps) {
  const { t } = useTranslation(['editor', 'common']);
  const [isGenerating, setIsGenerating] = useState({ start: false, end: false, storyboard: false });
  const [showImageSelector, setShowImageSelector] = useState<'start' | 'end' | 'storyboard' | null>(null);
  const [cropTarget, setCropTarget] = useState<{ url: string; type: 'start' | 'end' | 'storyboard' } | null>(null);
  const activeTab = activeFrameType;

  const { models } = useModels();
  const { alert, confirm } = useDialog();
  const { state } = useVideoEditor();
  const episodeAspectRatio = state.episode?.videoAspectRatio;

  const checkResourceStatus = async () => {
    try {
      const status = await videoClipResourceApi.getResourcesStatus(videoClip.id);
      // 根据 createdAt 倒序获取最新的记录
      const startImageResources = status.filter((r: any) => r.resourceType === ResourceType.START_IMAGE)
        .sort((a: any, b: any) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
      const endImageResources = status.filter((r: any) => r.resourceType === ResourceType.END_IMAGE)
        .sort((a: any, b: any) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
      const storyboardImageResources = status.filter((r: any) => r.resourceType === ResourceType.STORYBOARD_IMAGE)
        .sort((a: any, b: any) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
      const startImageResource = startImageResources.length > 0 ? startImageResources[0] : null;
      const endImageResource = endImageResources.length > 0 ? endImageResources[0] : null;
      const storyboardImageResource = storyboardImageResources.length > 0 ? storyboardImageResources[0] : null;

      const newGeneratingState = {
        start: startImageResource?.status === ResourceStatus.GENERATING || startImageResource?.status === ResourceStatus.PENDING,
        end: endImageResource?.status === ResourceStatus.GENERATING || endImageResource?.status === ResourceStatus.PENDING,
        storyboard: storyboardImageResource?.status === ResourceStatus.GENERATING || storyboardImageResource?.status === ResourceStatus.PENDING
      };

      const wasGenerating = isGenerating.start || isGenerating.end || isGenerating.storyboard;
      const isNowGenerating = newGeneratingState.start || newGeneratingState.end || newGeneratingState.storyboard;

      setIsGenerating(newGeneratingState);

      // 如果从生成中变为不生成，刷新数据
      if (wasGenerating && !isNowGenerating && refreshVideoClip) {
        await refreshVideoClip(videoClip.id);
      }
    } catch (error) {
      console.error('检查资源状态失败:', error);
    }
  };

  const currentImage = activeTab === 'start'
    ? videoClip.startImage
    : activeTab === 'end'
      ? videoClip.endImage
      : videoClip.storyboardImage;

  const currentGenerating = activeTab === 'start'
    ? isGenerating.start
    : activeTab === 'end'
      ? isGenerating.end
      : isGenerating.storyboard;

  const frameOptions: Array<{
    key: 'start' | 'end' | 'storyboard';
    label: string;
    imageUrl?: string;
  }> = [
    { key: 'start', label: t('editor:imagesTab.startFrame'), imageUrl: videoClip.startImage },
    { key: 'end', label: t('editor:imagesTab.endFrame'), imageUrl: videoClip.endImage },
    { key: 'storyboard', label: t('editor:imagesTab.storyboardImage'), imageUrl: videoClip.storyboardImage },
  ];

  // 组件挂载时立即检查状态
  useEffect(() => {
    checkResourceStatus();
  }, [videoClip.id]);

  // 轮询检查状态
  useEffect(() => {
    if (isGenerating.start || isGenerating.end || isGenerating.storyboard) {
      const interval = setInterval(checkResourceStatus, 2000);
      return () => clearInterval(interval);
    }
  }, [isGenerating.start, isGenerating.end, isGenerating.storyboard]);

  const handleUploadImage = async (url: string, type: 'start' | 'end' | 'storyboard') => {
    try {
      let updateData: any;
      if (type === 'start') {
        updateData = { startImage: url };
      } else if (type === 'end') {
        updateData = { endImage: url };
      } else {
        updateData = { storyboardImage: url };
      }

      await videoClipApi.updateVideoClip(videoClip.id, updateData);

      // 刷新 videoClip 数据
      if (refreshVideoClip) {
        await refreshVideoClip(videoClip.id);
      }
    } catch (error) {
      console.error('设置图片失败:', error);
      alert(t('editor:common.imageApplyFailed'));
    }
  };

  const handleClearImage = async (type: 'start' | 'end' | 'storyboard') => {
    const confirmed = await confirm(t('editor:imagesTab.clearImageConfirm'), t('editor:imagesTab.confirmClear'), 'warning');
    if (!confirmed) return;

    try {
      let updateData: any;
      if (type === 'start') {
        updateData = { startImage: '' };
      } else if (type === 'end') {
        updateData = { endImage: '' };
      } else {
        updateData = { storyboardImage: '' };
      }

      await videoClipApi.updateVideoClip(videoClip.id, updateData);

      if (refreshVideoClip) {
        await refreshVideoClip(videoClip.id);
      }
    } catch (error) {
      console.error('清除图片失败:', error);
      alert(t('editor:common.imageApplyFailed'));
    }
  };

  const handleSelectImage = async (image: IImageGeneration | IImageGeneration) => {
    if (!showImageSelector) return;

    try {
      const imageUrl = image.resultImageUrl;
      if (!imageUrl) {
        alert(t('editor:common.imageApplyFailed'));
        return;
      }

      await handleUploadImage(imageUrl, showImageSelector);
      setShowImageSelector(null);
    } catch (error) {
      console.error('选择图片失败:', error);
      alert(t('editor:common.imageApplyFailed'));
    }
  };





  return (
    <>
      <div className="flex flex-col h-full max-h-[90vh] bg-bg-surface text-gray-50">
        <div className="flex items-center justify-between gap-4 px-6 py-3 border-b border-border-subtle">
          <div className="flex items-baseline gap-3 min-w-0">
            <h2 className="text-lg font-semibold shrink-0">{t('editor:previewArea.images')}</h2>
            <p className="text-sm text-gray-400 truncate">{videoClip.title || t('editor:imagesTab.noStartFrame')}</p>
          </div>
          <button
            onClick={onRequestClose}
            className="inline-flex items-center justify-center w-10 h-10 rounded-lg border border-border-subtle bg-bg-elevated hover:bg-border-default text-gray-200 shrink-0"
            title={t('common:close', { defaultValue: '关闭' })}
          >
            <XMarkIcon className="w-4 h-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          <div className="max-w-5xl mx-auto space-y-6">
            <div className="grid grid-cols-3 gap-3">
              {frameOptions.map((option) => {
                const isActive = activeTab === option.key;

                return (
                  <button
                    key={option.key}
                    onClick={() => onFrameTypeChange?.(option.key)}
                    className={`rounded-2xl border p-3 text-left transition-all ${isActive
                      ? 'border-blue-500 bg-blue-500/10 shadow-[0_0_0_1px_rgba(59,130,246,0.35)]'
                      : 'border-border-subtle bg-bg-form hover:bg-bg-elevated hover:border-border-default'
                      }`}
                  >
                    <div className="aspect-video rounded-xl overflow-hidden bg-bg-elevated border border-border-subtle flex items-center justify-center mb-3">
                      {option.imageUrl ? (
                        <img
                          src={option.imageUrl}
                          alt={option.label}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="flex flex-col items-center justify-center text-gray-500">
                          <PhotoIcon className="w-6 h-6 mb-1" />
                          <span className="text-[11px]">{t('editor:imagesTab.noImagePlaceholder', { defaultValue: '暂无图片' })}</span>
                        </div>
                      )}
                    </div>
                    <div className="flex items-center justify-between gap-2">
                      <span className={`text-sm font-medium ${isActive ? 'text-blue-200' : 'text-gray-200'}`}>
                        {option.label}
                      </span>
                      <span className={`text-xs ${option.imageUrl ? 'text-emerald-400' : 'text-gray-500'}`}>
                        {option.imageUrl
                          ? t('editor:imagesTab.hasImage', { defaultValue: '已设置' })
                          : t('editor:imagesTab.noImageShort', { defaultValue: '未设置' })}
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>

            <div className="rounded-2xl border border-border-subtle bg-bg-surface p-5">
              <h3 className="text-sm font-medium text-gray-300 mb-4">{t('editor:imagesTab.selectImage')}</h3>
              <div className="flex flex-wrap gap-3">
                <button
                  onClick={() => setShowImageSelector(activeTab)}
                  className="px-4 py-2 bg-bg-elevated hover:bg-border-default text-gray-50 rounded-lg transition-colors flex items-center gap-2"
                >
                  <PhotoIcon className="w-4 h-4" />
                  {t('common:select')}
                </button>
                <ImageUpload
                  episodeId={videoClip.episodeId}
                  onUploadSuccess={(url) => handleUploadImage(url, activeTab)}
                  mode="button"
                  buttonClassName="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors flex items-center gap-2"
                  buttonText={t('common:upload')}
                />
                {currentImage && (
                  <button
                    onClick={() => setCropTarget({ url: currentImage, type: activeTab })}
                    className="px-4 py-2 bg-indigo-700 hover:bg-indigo-600 text-white rounded-lg transition-colors flex items-center gap-2"
                  >
                    <ScissorsIcon className="w-4 h-4" />
                    {t('editor:imagesTab.crop')}
                  </button>
                )}
              </div>

              {currentGenerating && (
                <p className="mt-3 text-xs text-indigo-300">
                  {t(`editor:imagesTab.generating${activeTab === 'start' ? 'StartFrame' : activeTab === 'end' ? 'EndFrame' : 'Storyboard'}`)}
                </p>
              )}
            </div>

            {currentImage && (
              <div className="rounded-2xl border border-red-900/40 bg-red-950/15 p-5">
                <button
                  onClick={() => handleClearImage(activeTab)}
                  className="inline-flex items-center justify-center gap-2 px-4 py-3 bg-red-600 hover:bg-red-700 text-white text-sm rounded-xl transition-colors"
                >
                  <TrashIcon className="w-4 h-4" />
                  {t('common:clear', { defaultValue: '清除' })}
                </button>
                <p className="mt-2 text-xs leading-5 text-gray-400">{t('editor:imagesTab.clearImageConfirm')}</p>
              </div>
            )}
          </div>
        </div>
      </div>

      <ImageSelector
        isOpen={!!showImageSelector}
        onClose={() => setShowImageSelector(null)}
        onSelect={handleSelectImage}
        models={models}
      />

      {cropTarget && (
        <ImageCropper
          imageUrl={cropTarget.url}
          aspectRatio={episodeAspectRatio}
          onConfirm={async (url) => {
            await handleUploadImage(url, cropTarget.type);
            setCropTarget(null);
          }}
          onCancel={() => setCropTarget(null)}
        />
      )}
    </>
  );
}
